import type { FC } from 'react'
import type { SceneProps } from '@/components/landing/games/scene-ui'
import type { CardValue } from '@/components/landing/games/playing-card'

import { useState } from 'react'
import { cn } from '@/lib/utils'
import { useSequence } from '@/hooks/use-sequence'
import { DealtCard, drawCard } from '@/components/landing/games/playing-card'
import { SceneBadge, SceneLabel } from '@/components/landing/games/scene-ui'

type Side = 'player' | 'banker'
type Outcome = Side | 'tie' | null

const SIDES: { key: Side, label: string, pays: string }[] = [
    { key: 'player', label: 'Player', pays: 'Pays 2×' },
    { key: 'banker', label: 'Banker', pays: 'Pays 1.95×' }
]

const points = (card: CardValue): number => (card.rank >= 10 ? 0 : card.rank)
const total = (hand: CardValue[]): number => hand.reduce((sum, card) => sum + points(card), 0) % 10

/** The fixed tableau: whether the banker draws, given its total and the player's third card. */
const bankerDraws = (bankerTotal: number, playerThird: CardValue | null): boolean => {
    if (playerThird === null) return bankerTotal <= 5

    const value = points(playerThird)

    if (bankerTotal <= 2) return true
    if (bankerTotal === 3) return value !== 8
    if (bankerTotal === 4) return value >= 2 && value <= 7
    if (bankerTotal === 5) return value >= 4 && value <= 7
    if (bankerTotal === 6) return value === 6 || value === 7
    return false
}

const POSTER_PLAYER: CardValue[] = [{ rank: 4, suit: 'hearts' }, { rank: 3, suit: 'clubs' }]
const POSTER_BANKER: CardValue[] = [{ rank: 13, suit: 'spades' }, { rank: 6, suit: 'diamonds' }]

const BaccaratScene: FC<SceneProps> = ({ active }) => {
    const [player, setPlayer] = useState<CardValue[]>(POSTER_PLAYER)
    const [banker, setBanker] = useState<CardValue[]>(POSTER_BANKER)
    const [outcome, setOutcome] = useState<Outcome>('player')

    useSequence(active, async ({ wait }) => {
        setPlayer([])
        setBanker([])
        setOutcome(null)

        if (!(await wait(600))) return

        const playerHand = [drawCard(), drawCard()]
        const bankerHand = [drawCard(), drawCard()]

        setPlayer([playerHand[0]])
        if (!(await wait(380))) return
        setBanker([bankerHand[0]])
        if (!(await wait(380))) return
        setPlayer([...playerHand])
        if (!(await wait(380))) return
        setBanker([...bankerHand])
        if (!(await wait(750))) return

        const natural = total(playerHand) >= 8 || total(bankerHand) >= 8

        if (!natural) {
            let third: CardValue | null = null

            if (total(playerHand) <= 5) {
                third = drawCard()
                playerHand.push(third)
                setPlayer([...playerHand])
                if (!(await wait(750))) return
            }

            if (bankerDraws(total(bankerHand), third)) {
                bankerHand.push(drawCard())
                setBanker([...bankerHand])
                if (!(await wait(750))) return
            }
        }

        const playerTotal = total(playerHand)
        const bankerTotal = total(bankerHand)

        setOutcome(playerTotal > bankerTotal ? 'player' : bankerTotal > playerTotal ? 'banker' : 'tie')
        await wait(2200)
    })

    const outcomeLabel = outcome === null
        ? 'Dealing'
        : outcome === 'tie'
            ? 'Tie pays 9×'
            : `${outcome === 'player' ? 'Player' : 'Banker'} wins`

    return (
        <div className='absolute inset-0 flex flex-col px-5 pb-5 pt-14'>
            <div className='grid flex-1 grid-cols-2 gap-3'>
                {SIDES.map(side => {
                    const hand = side.key === 'player' ? player : banker
                    const won = outcome === side.key || outcome === 'tie'

                    return (
                        <div
                            key={side.key}
                            className={cn(
                                'relative flex flex-col rounded-2xl border p-3.5 transition-all duration-500',
                                won ? 'border-ember/50 bg-ember/[0.06] shadow-[0_0_40px_-14px_rgba(255,91,31,0.7)]' : 'border-white/[0.06] bg-white/[0.02]',
                                outcome !== null && !won && 'opacity-55'
                            )}
                        >
                            <div className='flex items-center justify-between'>
                                <SceneLabel>{side.label}</SceneLabel>
                                <span className={cn('grid size-7 place-items-center rounded-full font-mono text-xs transition-colors duration-300', won ? 'bg-ember text-ink' : 'bg-white/[0.06] text-bone')}>
                                    {hand.length > 0 ? total(hand) : '·'}
                                </span>
                            </div>

                            <div className='mt-3 flex flex-1 items-center'>
                                {hand.map((card, index) => (
                                    <div key={index} className={cn('relative', index > 0 && '-ml-5')} style={{ zIndex: index }}>
                                        <DealtCard card={card} size='md' />
                                    </div>
                                ))}
                            </div>

                            <span className='font-mono text-[10px] text-steel'>{side.pays}</span>
                        </div>
                    )
                })}
            </div>

            <div className='mt-3 flex items-center justify-between gap-3'>
                <SceneBadge tone={outcome === null ? 'muted' : 'ember'}>{outcomeLabel}</SceneBadge>
                <span className='font-mono text-[10px] uppercase tracking-[0.12em] text-steel'>Fixed drawing rules</span>
            </div>
        </div>
    )
}

export default BaccaratScene
